import { Input, InputProps } from "@babackapp/ui/components/input";
import { forwardRef, useCallback } from "react";
import FormInputContainer from "./form-input-container";

type FormInputProps = Omit<InputProps, "disabled"> & {
  label: string;
  description?: string;
  disabled?: boolean | string;
  selectAllOnFocus?: boolean;
};

const FormInput = forwardRef(function FormInput(
  {
    label,
    description,
    disabled,
    selectAllOnFocus,
    onFocus,
    ...props
  }: FormInputProps,
  ref: React.Ref<HTMLInputElement>,
) {
  const _onFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (selectAllOnFocus) {
        e.target.select();
      }
      onFocus?.(e);
    },
    [selectAllOnFocus, onFocus],
  );

  // React 19 doesn't always fire onChange for paste on controlled inputs.
  // We intercept paste, prevent the browser's default, compute the new value,
  // then use the native prototype setter + a real `input` event so React's own
  // onChange pipeline fires with a proper event (same pattern as @testing-library/react).
  const _onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const input = e.currentTarget;
      const pastedText = e.clipboardData.getData('text/plain');
      const { selectionStart, selectionEnd, value } = input;
      const start = selectionStart ?? value.length;
      const end = selectionEnd ?? value.length;
      const newValue = value.slice(0, start) + pastedText + value.slice(end);

      e.preventDefault();

      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
      )?.set;
      if (nativeInputValueSetter) {
        nativeInputValueSetter.call(input, newValue);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    },
    [],
  );

  return (
    <FormInputContainer
      disabled={disabled}
      label={label}
      description={description}
    >
      <Input
        ref={ref}
        {...props}
        disabled={!!disabled}
        onFocus={_onFocus}
        onPaste={_onPaste}
      />
    </FormInputContainer>
  );
});

FormInput.displayName = "FormInput";

export default FormInput;
